/**
 * PlanCompareDialog — V14.99 : comparer un rendu IA au plan dans une grande
 * fenêtre dédiée (remplace la superposition translucide illisible).
 *  • Côte à côte (défaut) : rendu | image d'appui, même hauteur.
 *  • Superposé : plan par-dessus le rendu, opacité réglable + inversion.
 *  • Rideau : glissière gauche/droite.
 * Source de l'appui : « tel qu'envoyé à l'IA » (masque projeté si iso), masque
 * top-down, plan normalisé du lot.
 */
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Columns2, Layers, SplitSquareHorizontal, RotateCcw } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

/** → { src, error } : blob URL une fois chargée, ou error (HTTP status / réseau). */
const useBlobUrl = (url) => {
  const [state, setState] = useState({ src: null, error: null });
  useEffect(() => {
    setState({ src: null, error: null });
    if (!url) return undefined;
    let alive = true;
    let obj = null;
    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(String(r.status)))))
      .then((b) => { if (alive) { obj = URL.createObjectURL(b); setState({ src: obj, error: null }); } })
      .catch((e) => { if (alive) setState({ src: null, error: e?.message || 'erreur' }); });
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  return state;
};

const SOURCES = [
  { id: 'sent', label: 'Appui envoyé à l\'IA' },
  { id: 'raw', label: 'Masque top-down' },
  { id: 'plan', label: 'Plan normalisé' },
];
const MODES = [
  { id: 'side', label: 'Côte à côte', Icon: Columns2 },
  { id: 'overlay', label: 'Superposé', Icon: Layers },
  { id: 'curtain', label: 'Rideau', Icon: SplitSquareHorizontal },
];

const Seg = ({ items, value, onChange, testid }) => (
  <div className="inline-flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--c-border)' }} data-testid={testid}>
    {items.map(({ id, label, Icon }) => (
      <button key={id} type="button" onClick={() => onChange(id)} data-testid={`${testid}-${id}`}
              className="px-2.5 py-1 text-[11px] inline-flex items-center gap-1 transition-colors"
              style={value === id ? { background: 'var(--c-gold)', color: '#0F1115' } : { background: 'rgba(255,255,255,0.04)', color: 'var(--c-text)' }}>
        {Icon && <Icon className="w-3 h-3" />}{label}
      </button>
    ))}
  </div>
);

const PlanCompareDialog = ({ open, onClose, item, projectId }) => {
  const [source, setSource] = useState('sent');
  const [mode, setMode] = useState('side');
  const [opacity, setOpacity] = useState(0.6);
  const [invert, setInvert] = useState(true);
  const [curtain, setCurtain] = useState(50);
  const [zoom, setZoom] = useState(1);
  useEffect(() => { if (open) { setZoom(1); setCurtain(50); } }, [open, item?.job_id]);

  const renderUrl = item?.image_url ? `${BASE}${item.image_url}` : null;
  const planUrl = useMemo(() => {
    if (!item) return null;
    if (source === 'plan') return item.lot_id ? `${BASE}/api/lots/${item.lot_id}/normalized-plan` : null;
    return `${BASE}/api/projects/${projectId}/generations/${item.job_id}/binding?variant=${source}`;
  }, [item, source, projectId]);
  // Les deux images sont chargées UNE fois en blob (la projection caméra prend ~1 s
  // et l'endpoint du rendu n'est pas mis en cache) : changer de mode n'entraîne
  // plus aucun rechargement.
  const { src: renderSrc, error: renderError } = useBlobUrl(renderUrl);
  const { src: planSrc, error: planError } = useBlobUrl(planUrl);
  const planReady = (!!planSrc || !!planError) && (!!renderSrc || !!renderError);
  if (!item) return null;

  const planFilter = invert && source !== 'plan' ? 'invert(1)' : 'none';
  const frame = { background: '#0b0d11', border: '1px solid var(--c-border)', borderRadius: 12, overflow: 'hidden' };
  const imgStyle = { transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 120ms' };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-[96vw] w-[96vw] p-0 gap-0" style={{ background: 'var(--c-surface-0)', border: '1px solid var(--c-border)', maxHeight: '94vh' }}
                     data-testid="plan-compare-dialog">
        <DialogHeader className="px-4 py-3" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <DialogTitle className="text-[14px] flex items-center gap-3 flex-wrap" style={{ color: 'var(--c-text)', fontFamily: 'Outfit, sans-serif' }}>
            Conformité au plan · {item.lot_number || item.job_id.replace('gen_', '').slice(0, 6)}
            {item.params?.vue_angle && <span className="text-[11px] font-normal" style={{ color: 'var(--c-text-2)' }}>{item.params.vue_angle}</span>}
            {typeof item.fidelity_score === 'number' && (
              <span className="text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(217,160,91,0.16)', color: 'var(--c-gold)' }} data-testid="plan-compare-fidelity">
                fidélité {item.fidelity_score}%
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-2 flex items-center gap-3 flex-wrap" style={{ borderBottom: '1px solid var(--c-border)' }}>
          <Seg items={MODES} value={mode} onChange={setMode} testid="plan-compare-mode" />
          <Seg items={SOURCES} value={source} onChange={setSource} testid="plan-compare-source" />
          {source !== 'plan' && (
            <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: 'var(--c-text)' }}>
              <input type="checkbox" checked={invert} onChange={(e) => setInvert(e.target.checked)} data-testid="plan-compare-invert" />
              Murs en noir sur blanc
            </label>
          )}
          {mode === 'overlay' && (
            <label className="inline-flex items-center gap-2 text-[11px]" style={{ color: 'var(--c-text)' }}>
              Opacité plan
              <input type="range" min={0.1} max={1} step={0.05} value={opacity} onChange={(e) => setOpacity(+e.target.value)} className="w-32 accent-amber-400" data-testid="plan-compare-opacity" />
              <span className="tabular-nums w-8">{Math.round(opacity * 100)}%</span>
            </label>
          )}
          {mode === 'curtain' && (
            <label className="inline-flex items-center gap-2 text-[11px]" style={{ color: 'var(--c-text)' }}>
              Rideau
              <input type="range" min={0} max={100} step={1} value={curtain} onChange={(e) => setCurtain(+e.target.value)} className="w-40 accent-amber-400" data-testid="plan-compare-curtain" />
            </label>
          )}
          <label className="inline-flex items-center gap-2 text-[11px] ml-auto" style={{ color: 'var(--c-text)' }}>
            Zoom
            <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(+e.target.value)} className="w-28 accent-amber-400" data-testid="plan-compare-zoom" />
            <button type="button" onClick={() => setZoom(1)} className="hover:opacity-80" title="Réinitialiser"><RotateCcw className="w-3 h-3" /></button>
          </label>
        </div>

        <div className="p-4 overflow-hidden relative" style={{ height: 'calc(94vh - 120px)' }}>
          {!planReady && (
            <div className="absolute inset-4 z-10 flex items-center justify-center text-[12px] rounded-xl" style={{ background: 'rgba(11,13,17,0.75)', color: 'var(--c-text-2)' }} data-testid="plan-compare-loading">
              Chargement de l'image d'appui…
            </div>
          )}
          {planReady && planError && (
            <div className="absolute inset-4 z-10 flex items-center justify-center text-center px-6 rounded-xl" style={{ background: 'rgba(11,13,17,0.85)', color: '#FCD34D' }} data-testid="plan-compare-source-unavailable">
              <div className="text-[12.5px] space-y-1">
                <div className="font-semibold">Source « {SOURCES.find((x) => x.id === source)?.label} » indisponible{planError === '404' ? ' pour ce lot' : ''}.</div>
                <div style={{ color: 'var(--c-text-2)' }}>
                  {source === 'plan'
                    ? 'Aucun plan normalisé enregistré : utilisez « Appui envoyé à l\'IA » ou « Masque top-down ».'
                    : 'Ce rendu ne porte pas d\'image d\'appui (génération hors éditeur de plan) : essayez « Plan normalisé ».'}
                </div>
              </div>
            </div>
          )}
          {mode === 'side' && (
            <div className="grid grid-cols-2 gap-4 h-full">
              <figure className="relative h-full flex flex-col" style={frame} data-testid="plan-compare-render">
                <img src={renderSrc} alt="Rendu IA" className="flex-1 min-h-0 w-full object-contain" style={imgStyle} />
                <figcaption className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>Rendu IA</figcaption>
              </figure>
              <figure className="relative h-full flex flex-col" style={{ ...frame, background: invert || source === 'plan' ? '#ffffff' : '#000' }} data-testid="plan-compare-plan">
                <img src={planSrc} alt="Plan de référence" className="flex-1 min-h-0 w-full object-contain" style={{ ...imgStyle, filter: planFilter }} />
                <figcaption className="absolute top-2 left-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.6)', color: '#fff' }}>
                  {SOURCES.find((s) => s.id === source)?.label}
                </figcaption>
              </figure>
            </div>
          )}
          {mode === 'overlay' && (
            <div className="relative h-full flex items-center justify-center" style={frame} data-testid="plan-compare-overlay">
              <img src={renderSrc} alt="Rendu IA" className="absolute inset-0 m-auto max-h-full max-w-full object-contain" style={imgStyle} />
              <img src={planSrc} alt="Plan" className="absolute inset-0 m-auto max-h-full max-w-full object-contain pointer-events-none"
                   style={{ ...imgStyle, opacity, filter: planFilter, mixBlendMode: invert || source === 'plan' ? 'multiply' : 'screen' }} />
            </div>
          )}
          {mode === 'curtain' && (
            <div className="relative h-full" style={frame} data-testid="plan-compare-curtain-view">
              <img src={renderSrc} alt="Rendu IA" className="absolute inset-0 m-auto max-h-full max-w-full object-contain" style={imgStyle} />
              <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - curtain}% 0 0)`, background: invert || source === 'plan' ? '#fff' : '#000' }}>
                <img src={planSrc} alt="Plan" className="absolute inset-0 m-auto max-h-full max-w-full object-contain" style={{ ...imgStyle, filter: planFilter }} />
              </div>
              <div className="absolute top-0 bottom-0 w-0.5" style={{ left: `${curtain}%`, background: 'var(--c-gold)' }} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanCompareDialog;
